/**
 * Variant Picker Subscription Price Module
 * Updates variant button prices when SKIO subscription is selected
 *
 * - Listens to SKIO's 'skio::update-selling-plan' event
 * - Updates variant button prices to show subscription pricing
 * - Reverts to original prices when one-time purchase is selected
 */

class VariantPickerSubscriptionPrice {
  constructor() {
    this.originalPrices = new Map();
    this.init();
  }

  init() {
    // Store original prices on page load
    this.storeOriginalPrices();

    // Listen for SKIO plan changes
    document.addEventListener('skio::update-selling-plan', (event) => {
      this.updateVariantButtonPrices(event);
    });

    // Listen for variant changes to re-store original prices
    document.addEventListener('variant:update', () => {
      setTimeout(() => this.storeOriginalPrices(), 100);
    });
  }

  /**
   * Store original price HTML for each variant button
   */
  storeOriginalPrices() {
    const priceWrappers = document.querySelectorAll('.variant-option__button-label__price-wrapper');
    priceWrappers.forEach(wrapper => {
      const input = wrapper.closest('.variant-option__button-label')?.querySelector('input[data-variant-id]');
      if (input) {
        const variantId = input.dataset.variantId;
        if (!this.originalPrices.has(variantId)) {
          this.originalPrices.set(variantId, wrapper.innerHTML);
        }
      }
    });
  }

  /**
   * Update variant button prices based on SKIO selection
   */
  updateVariantButtonPrices(event) {
    const { variant, sellingPlan } = event.detail;
    const skioComponent = event.target;

    // Get all variant buttons
    const variantButtons = document.querySelectorAll('.variant-option__button-label');

    variantButtons.forEach(button => {
      const input = button.querySelector('input[data-variant-id]');
      if (!input) return;

      const variantId = input.dataset.variantId;
      const priceWrapper = button.querySelector('.variant-option__button-label__price-wrapper');
      if (!priceWrapper) return;

      // If no selling plan (one-time purchase), revert to original prices
      if (!sellingPlan) {
        const originalHTML = this.originalPrices.get(variantId);
        if (originalHTML) {
          priceWrapper.innerHTML = originalHTML;
        }
        return;
      }

      const product = skioComponent?.product;
      const variantData =
        String(variant?.id) === String(variantId)
          ? variant
          : product?.variants?.find(v => String(v.id) === String(variantId)) || this.getVariantData(variantId);
      if (!variantData) return;

      const allocation = this.pickAllocation(variantData, sellingPlan, product);
      if (!allocation) {
        const originalHTML = this.originalPrices.get(variantId);
        if (originalHTML) priceWrapper.innerHTML = originalHTML;
        return;
      }

      const subscriptionPrice = allocation.price;

      // Whether to badge is a property of the picker, not of whatever the server
      // happened to render. A variant whose only discount is the subscription
      // has no compare_at_price, so it gets no server-rendered badge - reading
      // the badge back out of the DOM would leave that variant silent forever.
      const showPercentOff =
        button.closest('variant-picker')?.dataset.showPercentOff === 'true';

      // MSRP baseline: compare_at_price when it is a genuine strike price,
      // otherwise the one-time price. Same baseline as the hero badge.
      const compareAt = parseInt(variantData.compare_at_price, 10);
      const baseline =
        showPercentOff && !isNaN(compareAt) && compareAt > variantData.price
          ? compareAt
          : variantData.price;

      if (subscriptionPrice >= baseline) return;

      let html =
        `<span class="variant-option__button-label__compare-price">${this.formatMoney(baseline)}</span>` +
        `<span class="variant-option__button-label__price">${this.formatMoney(subscriptionPrice)}</span>`;

      if (showPercentOff) {
        const percent = Math.round(((baseline - subscriptionPrice) / baseline) * 100);
        if (percent > 0) {
          html +=
            `<span class="tag tag--sale variant-option__button-label__percent-off" data-variant-percent-off` +
            ` data-variant-id="${variantId}" data-compare-at="${baseline}">${percent}% OFF</span>`;
        }
      }

      priceWrapper.innerHTML = html;
    });
  }

  /**
   * Each pack has its own plans, so a variant that lacks the selected plan
   * shows its own regular Skio plan (not a trial, not a non-Skio group).
   */
  pickAllocation(variantData, sellingPlan, product) {
    const allocations = variantData.selling_plan_allocations || [];
    const exact = allocations.find(a => a.selling_plan_id === sellingPlan.id);
    if (exact) return exact;

    const skioGroupIds = new Set(
      (product?.selling_plan_groups || [])
        .filter(g => g.app_id === 'SKIO' && !/hidden-|dynamic box subscription/i.test(g.name))
        .map(g => g.id)
    );
    const candidates = skioGroupIds.size
      ? allocations.filter(a => skioGroupIds.has(a.selling_plan_group_id))
      : allocations;

    return candidates.find(a => (a.price_adjustments || []).length <= 1) || candidates[0] || null;
  }

  /**
   * Get variant data from the page's JSON
   */
  getVariantData(variantId) {
    // Try to find variant data from product JSON on page
    const productJsonScript = document.querySelector('script[type="application/json"][data-product-json]');
    if (productJsonScript) {
      try {
        const productData = JSON.parse(productJsonScript.textContent);
        return productData.variants?.find(v => String(v.id) === String(variantId));
      } catch (e) {}
    }

    // Try SKIO's selectedvariant attribute
    const skioPicker = document.querySelector('skio-plan-picker');
    if (skioPicker) {
      try {
        const selectedVariant = JSON.parse(skioPicker.getAttribute('selectedvariant'));
        if (String(selectedVariant.id) === String(variantId)) {
          return selectedVariant;
        }
      } catch (e) {}

      // Try SKIO's variants attribute for all variants
      try {
        const variants = JSON.parse(skioPicker.getAttribute('variants'));
        return variants?.find(v => String(v.id) === String(variantId));
      } catch (e) {}
    }

    // Try variant picker's embedded JSON
    const variantPickerScripts = document.querySelectorAll('variant-picker script[type="application/json"]');
    for (const script of variantPickerScripts) {
      try {
        const variantData = JSON.parse(script.textContent);
        if (String(variantData.id) === String(variantId)) {
          return variantData;
        }
      } catch (e) {}
    }

    return null;
  }

  /**
   * Format price in cents to currency string (rounded, no decimals)
   */
  formatMoney(cents) {
    const dollars = Math.round(cents / 100);
    return `$${dollars}`;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new VariantPickerSubscriptionPrice();
  });
} else {
  new VariantPickerSubscriptionPrice();
}
